import { useState } from "react";
import { Link } from "react-router-dom";

import { useSetWatch, useUnwatch, useWatchlist } from "../api/queries";
import type { WatchRow } from "../api/types";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { ModePill } from "../components/ModePill";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { VariantPrices } from "../components/Variation";
import { adena, ago, plural, signed, when } from "../format";
import { itemHref } from "../search/nav";
import { delta, isStale } from "./marketSort";
import { sortWatchRows } from "./watchSort";
import { commitTarget } from "./watchTarget";

/** What the target box asks for, in words.
 *
 *  Never a bare operator. `buy` and `sell` read backwards in this domain — a
 *  buy row is somebody else's bid — so a "≤" that silently flips with the
 *  column is exactly where a reader would mis-set a target and not find out
 *  until the wrong thing pinged them. */
const ASKS: Record<WatchRow["mode"], string> = {
  buy: "when someone bids at least",
  sell: "when the asking price drops to",
  craft: "when the fee drops to",
};

/** The target box: controlled, and put back from the server whenever a write
 *  does not land.
 *
 *  `defaultValue` was the bug it used to have. React applies that at mount
 *  only, and the row key is stable across the 60s poll, so this cell never
 *  remounts — a rejected write left the number the user typed sitting in the
 *  box, looking saved, for as long as the page stayed open. The page's error
 *  band did not cover it: `set` is shared by every row, so a later success
 *  anywhere in the table cleared the band and left the lie on its own.
 *  NpcPricePanel is the precedent, and it is a draft in state, an explicit
 *  `value`, and a reset — not just the band.
 *
 *  `set` is passed in rather than hooked up here: one mutation for the table,
 *  not one per row. A row-held mutation would put the failure state on the row
 *  that failed, which is the one place a reader is not looking once they have
 *  tabbed on — and it would make the page's error band depend on which row
 *  happened to be rendered. Typed off the hook so this module keeps its
 *  distance from react-query; api/queries.ts is the only seam that knows it. */
function TargetCell({ row, set }: {
  row: WatchRow; set: ReturnType<typeof useSetWatch>;
}) {
  const stored = row.target_price === null ? "" : String(row.target_price);
  const [draft, setDraft] = useState(stored);
  // What the server last said. Adjusting state during render is React's own
  // answer to "prop changed, derived state must follow": a 60s poll can bring
  // a target someone changed in another tab, and an input still showing the
  // old one is the same lie as an input showing a write that failed.
  const [synced, setSynced] = useState(stored);
  if (synced !== stored) {
    setSynced(stored);
    setDraft(stored);
  }

  return (
    <input
      className="target"
      type="number"
      min={1}
      step={1}
      value={draft}
      aria-label={`${ASKS[row.mode]} — ${row.item}`}
      title={`Tell me ${ASKS[row.mode]} this.`}
      placeholder="—"
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      onBlur={(e) => {
        // `validity.badInput` is read off the element and handed over, because
        // watchTarget.ts is the module that decides and it has no element to
        // read. Read synchronously here, while the event is still being
        // dispatched and `currentTarget` is still this input.
        const commit = commitTarget(
          draft, e.currentTarget.validity.badInput, row.target_price);
        if (commit.kind === "keep") return;
        if (commit.kind === "reset") {
          setDraft(stored);
          return;
        }
        set.mutate({ itemId: row.item_id, mode: row.mode, target: commit.target }, {
          // The band says something went wrong; this says what the value
          // still is. Without it the box reads as saved for as long as the
          // page stays open.
          onError: () => setDraft(stored),
        });
      }}
    />
  );
}

/** The index of what this account is waiting for.
 *
 *  One row per watched series rather than per item, which is what makes the
 *  target cell possible: a target points at one price series, and a row that
 *  carried all three of an item's modes would have nowhere to put it. */
export function Watch() {
  const watchlist = useWatchlist({ poll: true });
  const set = useSetWatch();
  const unwatch = useUnwatch();

  // `isLoadingError`, not `isError`: react-query keeps the last good `data`
  // when a *background* refetch fails, and this is the one page in the app
  // that refetches in the background. `isError` here threw a populated table
  // away and put an error band in its place for up to a minute because one
  // poll hit an API container that was restarting — with the correct rows
  // sitting in the cache the whole time. Only an error with nothing behind it
  // takes the page; an error with rows behind it says so above them.
  if (watchlist.isLoadingError) {
    return (
      <>
        <TitleBand title="Watching" />
        <ErrorBand
          message="Could not load your watchlist."
          hint="The server may be restarting. Reload in a moment."
        />
      </>
    );
  }
  if (!watchlist.data) {
    return <><TitleBand title="Watching" /><Skeleton rows={6} /></>;
  }

  const rows = sortWatchRows(watchlist.data.rows);
  const hits = rows.filter((row) => row.fulfilled).length;

  if (rows.length === 0) {
    return (
      <>
        <TitleBand title="Watching" />
        <Panel primary>
          <Empty>
            Nothing watched yet. Open an item and star the side you care about —
            its <Link to="/">market row</Link> or its own page — and it appears
            here with whatever the last scan said about it.
          </Empty>
        </Panel>
      </>
    );
  }

  return (
    <>
      {/* The explicit plural is not decoration: format.ts's `plural` appends
          an "s" unless told otherwise, and "seriess" is what this prints
          without it. */}
      <TitleBand
        title="Watching"
        note={`${plural(rows.length, "series", "series")} · ${plural(hits, "hit")}`}
      />
      <Panel primary>
        {/* Above the table and never instead of it: these rows are the last
            thing the server actually said, which is worth more than the fact
            that the next poll missed. No "reload" hint, because reloading is
            what the page is already doing — the band goes on its own when a
            poll lands. */}
        {watchlist.isRefetchError ? (
          <ErrorBand
            message="Could not refresh your watchlist."
            hint="These are the rows the last good read returned; the page keeps trying."
          />
        ) : null}
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Side</th>
                <th className="num">Best</th>
                <th className="num">vs last</th>
                <th className="num">Target</th>
                <th>State</th>
                <th className="num">Last seen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.item_id}-${row.mode}`} className={row.fulfilled ? "hit" : undefined}>
                  <td>
                    <Link to={itemHref(row.item_id, row.item)}>{row.item}</Link>
                    {/* The quote is a variant's, because nobody has ever listed
                        this item plain. It matters more here than on any other
                        table: `fulfilled` measures the target beside it against
                        that figure, so the hit in the State column is a hit on a
                        price nobody offered for the plain item. */}
                    <VariantPrices from={row.from_variants} />
                  </td>
                  <td><ModePill mode={row.mode} /></td>
                  <td className={`num ${row.mode}`}>{adena(row.quote?.best ?? null)}</td>
                  <td
                    className={`num ${delta(row.quote) === null ? "dim" : ""}`}
                    title="Against the previous scan that saw this item"
                  >
                    {signed(delta(row.quote))}
                  </td>
                  <td className="num"><TargetCell row={row} set={set} /></td>
                  {/* The word, not only the wash. DESIGN.md's rule for the mode
                      pills holds here for the same reason: the state has to
                      survive greyscale, forced-colors, and any colour vision. */}
                  <td>{row.fulfilled ? <span className="hit-word">hit</span> : ""}</td>
                  <td
                    className={`num ${row.quote === null || isStale(row.quote.observed_at) ? "dim" : ""}`}
                    title={row.quote === null ? undefined : when(row.quote.observed_at)}
                  >
                    {row.quote === null ? "never offered" : ago(row.quote.observed_at)}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="link"
                      aria-label={`Stop watching ${row.item} ${row.mode}`}
                      onClick={() => unwatch.mutate({ itemId: row.item_id, mode: row.mode })}
                    >
                      remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Under the table and inside the panel, where NpcPricePanel puts the
            same band: a write that did not land has to be visible from the row
            that was being edited, and the list above it is still the truth. */}
        {set.isError || unwatch.isError ? (
          <ErrorBand
            message="Could not save that change."
            hint="Your list is unchanged. Reload and try again."
          />
        ) : null}
      </Panel>
    </>
  );
}
